export const LUA_PACKAGE_LIMITS = Object.freeze({ maxModules: 128, maxModuleBytes: 256 * 1024, maxTotalBytes: 1024 * 1024, maxConcurrentLoads: 8 })

export function moduleAssetPath(name) {
  const value = String(name || '').trim()
  if (!/^src(?:\.[A-Za-z_][A-Za-z0-9_]*)+$/.test(value)) throw new Error('invalid_lua_module_name')
  return `${value.replaceAll('.', '/')}.lua`
}

export function scanLuaRequires(source) {
  const out = []
  const seen = new Set()
  const re = /\brequire\s*(?:\(\s*)?(['"])([^'"]+)\1\s*\)?/g
  let match
  while ((match = re.exec(String(source || ''))) !== null) {
    const name = String(match[2] || '').trim()
    moduleAssetPath(name)
    if (!seen.has(name)) { seen.add(name); out.push(name) }
  }
  return out
}

function limiter(maxConcurrent) {
  const limit = Math.max(1, Math.floor(Number(maxConcurrent) || 1))
  const queue = []
  let active = 0

  const drain = () => {
    while (active < limit && queue.length > 0) {
      const task = queue.shift()
      active += 1
      Promise.resolve()
        .then(task.run)
        .then(task.resolve, task.reject)
        .finally(() => { active -= 1; drain() })
    }
  }

  return run => new Promise((resolve, reject) => {
    queue.push({ run, resolve, reject })
    drain()
  })
}

function assertAcyclic(graph, roots) {
  const state = new Map()

  function visit(name) {
    const current = state.get(name)
    if (current === 'visiting') throw new Error(`lua_module_cycle:${name}`)
    if (current === 'visited') return
    state.set(name, 'visiting')
    for (const dependency of graph.get(name) || []) visit(dependency)
    state.set(name, 'visited')
  }

  for (const root of roots) visit(root)
}

export async function preloadLuaModules(entrySource, loadAsset, limits = LUA_PACKAGE_LIMITS) {
  if (typeof loadAsset !== 'function') throw new Error('lua_module_loader_required')
  const modules = new Map()
  const graph = new Map()
  const discovered = new Set()
  const load = limiter(limits.maxConcurrentLoads ?? LUA_PACKAGE_LIMITS.maxConcurrentLoads)
  let totalBytes = new TextEncoder().encode(String(entrySource || '')).length

  async function discover(name) {
    if (discovered.has(name)) return
    if (discovered.size >= limits.maxModules) throw new Error('lua_module_count_exceeded')
    discovered.add(name)

    const path = moduleAssetPath(name)
    const source = String(await load(() => loadAsset(path)))
    const bytes = new TextEncoder().encode(source).length
    if (bytes <= 0 || bytes > limits.maxModuleBytes) throw new Error(`lua_module_size:${name}`)
    totalBytes += bytes
    if (totalBytes > limits.maxTotalBytes) throw new Error('lua_package_size_exceeded')

    const dependencies = scanLuaRequires(source)
    modules.set(name, source)
    graph.set(name, dependencies)
    await Promise.all(dependencies.map(discover))
  }

  const roots = scanLuaRequires(entrySource)
  await Promise.all(roots.map(discover))
  assertAcyclic(graph, roots)
  return modules
}
